import axios, { AxiosError } from "axios";


import { useSetRecoilState, useRecoilValue, } from 'recoil';

import { currentUserSessionSetAuthentication, currentUserSessionAtom } from './store';

import { globalConfig } from "config";


import { useTranslation } from "react-i18next";

import { useSnackbar } from 'notistack';
import { useEffect } from "react";


type ICustomAxiosError = {
  response: { data: { data: any, detail: string, messages: string[], succeeded: boolean } }
};


// ---- Module-level holder, always reflects the latest auth state ----
// Updated via the useEffect below, read directly inside the request interceptor
// so it's never stale, no matter when/where the axios instance was created.
const authState: { token: string; refreshToken: string; isAuthenticated: boolean } = {
  token: '',
  refreshToken: '',
  isAuthenticated: false,
};

// Single shared axios instance — created once, not recreated per hook call.
// Headers that don't depend on auth state can stay static; auth is injected per-request.
const _axios = axios.create({
  //baseURL: globalConfig.get().apiUrl,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
  },
});

let interceptorsAttached = false;


// eslint-disable-next-line import/no-anonymous-default-export
export default () => {

  const { enqueueSnackbar } = useSnackbar();

  const currentUserSession = useRecoilValue(currentUserSessionAtom);
  const { token, refreshToken, isAuthenticated } = currentUserSession;
  const disconnectUser = useSetRecoilState(currentUserSessionSetAuthentication);

  const { t, i18n } = useTranslation();


  // Keep the module-level holder in sync with the latest Recoil state on every render
  // where it changed. This is what makes the interceptor always see the current token.
  useEffect(() => {
    authState.token = token;
    authState.refreshToken = refreshToken;
    authState.isAuthenticated = isAuthenticated;
  }, [token, refreshToken, isAuthenticated]);

  // Keep the language header current too, since it can change at runtime (i18n.changeLanguage)
  useEffect(() => {
    _axios.defaults.headers.common['Accept-Language'] = i18n.language.includes('en') ? 'en-US' : 'fr-FR';
  }, [i18n.language]);


  /*const _axios = axios.create({
    baseURL: globalConfig.get().apiUrl,

    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      
      'Accept-Language': i18n.language.includes('en') ? 'en-US' : 'fr-FR',
      'Authorization': isAuthenticated ? `Bearer ${token}` : ''
    }
  }); */

  // axios.defaults.headers.get['Accepts'] = 'application/json'
  // axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*'

  // Attach interceptors exactly once (module-level guard), not once per hook call —
  // otherwise every call to useAxios() would stack another duplicate interceptor.
  if (!interceptorsAttached) {
    interceptorsAttached = true;

    _axios.interceptors.request.use((config) => {

      if (!config.baseURL) {
        config.baseURL = globalConfig.get().apiUrl;
      }
      
      if (authState.isAuthenticated && authState.token) {
        config.headers = config.headers ?? {};
        config.headers['Authorization'] = `Bearer ${authState.token}`;
      }
      return config;
    });

    _axios.interceptors.response.use(response => response,
      async (error: AxiosError) => {

        const status = error.response ? error.response.status : null;
        // Reject promise if usual error
        if (status !== 401) {
          if (axios.isAxiosError(error)) {
            console.log(error);
            const customError: ICustomAxiosError = error as unknown as ICustomAxiosError;

            if (customError.response && customError.response.data && customError.response.data.detail)
              enqueueSnackbar(customError.response.data.detail, {
                variant: 'error',
                anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 3500
              });
            else if (customError.response && customError.response.data && customError.response.data.messages)
              enqueueSnackbar(customError.response.data.messages.join("\n"), {
                variant: 'error',
                anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 3500
              });
          }

          return new Promise((resolve, reject) => {
            reject(error);
          });
        }

        // Logout user if token refresh didn't work or user is disabled
        if (error.config.url === '/security/user/refreshtoken' /*|| error?.response?.message === 'Account_is_disabled.'*/) {

          disconnectUser(currentUserSession);
          return new Promise((resolve, reject) => {
            reject(error);
          });
        }

        try {
          //const token_1 = await getNewToken(token, refreshToken, refreshUserToken, disconnectUser);
          const token_1 = await getNewToken(token, refreshToken, (accessToken: string) => { },
            () => { });
          // New request with new token
          const config = error.config;
          //config.headers['Authorization'] = `Bearer ${token}`;

          _axios.defaults.headers.common["Authorization"] = `Bearer ${token_1}`;
          return await new Promise((resolve_2, reject_2) => {
            axios.request(config).then(response => {
              resolve_2(response);

            }).catch((error_1) => {
              reject_2(error_1);
            });
          });
        } catch (err) {
          Promise.reject(error);
        }
      });
  }

  return _axios;
};

const getNewToken = (token: string, refreshToken: string, refreshUserToken: (accessToken: string) => void, disconnectUser: () => void) => new Promise((resolve, reject) => {

  axios.post(`${globalConfig.get().apiUrl}/security/user/refreshtoken`, {
    accessToken: token,
    refreshToken: refreshToken
  },).then(reponse => {

    if (reponse.data === '') disconnectUser();

    refreshUserToken(reponse.data);
    resolve(reponse);
  })
    .catch(error => {

      reject(error);
    });
});